import type { TFunction } from 'i18next';
import type { ICourse } from './courseSchedule';
import {
  coerceDivisionScheme,
  resolveDivisionSegments,
  type ChosenSchemeKind,
} from '../schemaForm/fields/divisionScheme';

/**
 * Shared pricing helpers for the member-facing course (kroužek) surfaces.
 * Prices arrive as numbers or decimal strings from the backend, so every
 * helper normalizes via parseFloat before formatting. The CZK convention
 * (number.toLocaleString(locale) + " CZK") mirrors the event price display
 * in PublicEventsListPage.
 */

/** Parse a number|string|null price into a finite number, or null when absent/invalid. */
const toNumber = (value: number | string | null | undefined): number | null => {
  if (value == null) return null;
  const num = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(num) ? num : null;
};

/**
 * Format a price into a localized "X CZK" string, or null when there is no
 * usable value. Matches the event card price formatting (toLocaleString + CZK).
 */
export const formatPrice = (
  value: number | string | null | undefined,
  locale: string
): string | null => {
  const num = toNumber(value);
  if (num == null) return null;
  return `${num.toLocaleString(locale || undefined)} CZK`;
};

export interface IPricingOption {
  label: string;
  price: string;
}

/**
 * The priced rows of ONE non-season membership, labelled by what distinguishes
 * the rows from each other (interval / duration / entry count) — NOT by the
 * membership name, which the caller renders as a heading, and NOT by the season
 * term, which the pricing modal already shows once in its header.
 *
 * `buildPricingOptions` flattens every membership into one list with the name
 * baked into each label; that reads as a wall of near-identical rows and made it
 * impossible to tell which price belonged to which plan. Callers that group by
 * membership should use this instead.
 */
export const buildMembershipRows = (
  membership: NonNullable<ICourse['memberships']>[number],
  t: TFunction,
  locale: string
): IPricingOption[] => {
  const rows: IPricingOption[] = [];
  const options = membership.priceOptions;

  if (options && options.length > 0) {
    for (const option of options) {
      const price = formatPrice(option.amount, locale);
      if (!price) continue;
      let label: string;
      if (membership.type === 'entry' && option.entryCount != null) {
        label = t('courses.entries', { count: option.entryCount });
      } else if (option.interval) {
        label = t(`courses.interval.${option.interval}`, option.interval);
      } else if (option.durationDays != null) {
        label = t('courses.durationDays', { count: option.durationDays });
      } else {
        label = t('courses.priceStandard');
      }
      rows.push({ label, price });
    }
    return rows;
  }

  // Backward-compat: no priceOptions but a positive flat price → one row.
  const price = formatPrice(membership.price, locale);
  if (price && toNumber(membership.price)) {
    rows.push({ label: t('courses.priceStandard'), price });
  }
  return rows;
};

/**
 * The cheapest amount a single membership can be bought for, across every option
 * it offers (calendar intervals, time tiers, entry packs, or — for a `season`
 * plan — the whole season and each division segment). Used for the "from X"
 * summary on a plan's tab. Returns null when nothing on it is priced.
 */
export const planCheapest = (
  membership: NonNullable<ICourse['memberships']>[number]
): number | null => {
  const values: number[] = [];

  if (membership.type === 'season') {
    const scheme = coerceDivisionScheme(membership.divisionScheme, toNumber(membership.price) ?? 0);
    const full = toNumber(scheme.fullPrice);
    if (full != null && full > 0) values.push(full);
    for (const lane of scheme.schemes) {
      for (const raw of lane.prices || []) {
        const amount = toNumber(raw);
        if (amount != null && amount > 0) values.push(amount);
      }
    }
  } else {
    for (const option of membership.priceOptions || []) {
      if (Number.isFinite(option.amount) && option.amount > 0) values.push(option.amount);
    }
    if (values.length === 0) {
      const flat = toNumber(membership.price);
      if (flat != null && flat > 0) values.push(flat);
    }
  }

  return values.length > 0 ? Math.min(...values) : null;
};

/** The drop-in price as a formatted string, or null when unset or zero. */
export const perSessionPrice = (
  course: ICourse,
  locale: string
): string | null => {
  const amount = toNumber(course.pricePerSession);
  if (amount == null || amount <= 0) return null;
  return formatPrice(amount, locale);
};

/* ------------------------------------------------------------------ *
 * Season-division tiers
 * ------------------------------------------------------------------ */

/** One buyable stretch of a season: a concrete period at a concrete price. */
export interface IPricingSegment {
  /** 'full' for the whole season, else the lane kind ('halves', 'quarters', …). */
  scheme: ChosenSchemeKind;
  /** 0-based position within its lane; null for 'full'. */
  index: number | null;
  amount: number;
  /** Localized "1. 9. – 31. 1.", or null when the course has no term. */
  period: string | null;
  /** Share of the whole season this stretch covers, 0..1. */
  fraction: number;
  /** Price per 30 days, for comparing stretches of different lengths. */
  monthlyRate: number | null;
}

/**
 * A member's concrete pick from the pricing modal, carried into the join flow so
 * an open-enrollment sign-up can enrol and assign the chosen stretch in one go.
 * Mirrors the body of `POST /api/my/members/:id/memberships`.
 */
export interface IPricingSelection {
  membershipId: number;
  segmentScheme: ChosenSchemeKind;
  /** null for the whole-season stretch. */
  segmentIndex: number | null;
}

/** One lane of the division (the whole season, or a set of equal-ish parts). */
export interface IPricingTier {
  scheme: ChosenSchemeKind;
  segments: IPricingSegment[];
  /** Cost of covering the season via this lane (sum of its segments). */
  total: number;
  /**
   * True for the cheapest way(s) to cover the whole season. Several lanes tie
   * when a plan prices its splits proportionally, in which case they are all
   * flagged — the badge must never appear on a lane that costs more.
   */
  isBest: boolean;
  /** Extra paid versus the cheapest lane; 0 when `isBest`. */
  extraVsBest: number;
}

/** Sub-CZK differences are rounding noise, not a real price difference. */
const PRICE_EPSILON = 0.5;

const DAYS_PER_MONTH = 30;

/** Format a segment's date window using the member's locale, e.g. "1. 9. – 31. 1.". */
const formatPeriod = (
  start: Date | null,
  end: Date | null,
  locale: string
): string | null => {
  if (!start || !end) return null;
  // Matches the season header (seasonTermLabel): "Sep 1 – Jun 30", not "9/1 – 6/30".
  // Segment dates come from UTC day arithmetic, so format in UTC to avoid drift.
  const fmt = new Intl.DateTimeFormat(locale || undefined, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  return `${fmt.format(start)} – ${fmt.format(end)}`;
};

/**
 * Expand a `season` membership into its purchasable tiers: the whole season
 * first, then each division lane (halves, quarters, …).
 *
 * This is what replaces the single collapsed "cheapest amount" row — that row
 * showed e.g. 830 CZK (one quarter) under a label naming the full term, with
 * no way to tell what the money bought.
 *
 * Returns [] for a non-season membership or a scheme with no priced segments,
 * so callers can fall back to `buildPricingOptions`.
 */
export const buildSeasonTiers = (
  membership: { type: string; divisionScheme?: unknown; price?: number | string | null },
  termStart: string | null | undefined,
  termEnd: string | null | undefined,
  locale: string
): IPricingTier[] => {
  if (membership.type !== 'season') return [];

  const scheme = coerceDivisionScheme(membership.divisionScheme, toNumber(membership.price) ?? 0);
  const resolved = resolveDivisionSegments(scheme, termStart, termEnd);
  const span = resolved.span;

  const rate = (amount: number, days: number): number | null =>
    days > 0 ? (amount / days) * DAYS_PER_MONTH : null;

  const tiers: IPricingTier[] = [];

  const fullAmount = toNumber(scheme.fullPrice);
  if (fullAmount != null && fullAmount > 0) {
    tiers.push({
      scheme: 'full',
      total: fullAmount,
      isBest: false,
      extraVsBest: 0,
      segments: [{
        scheme: 'full',
        index: null,
        amount: fullAmount,
        period: formatPeriod(resolved.full.startDate, resolved.full.endDate, locale),
        fraction: 1,
        monthlyRate: rate(fullAmount, resolved.full.days),
      }],
    });
  }

  for (const lane of scheme.schemes) {
    const resolvedLane = resolved.schemes[lane.kind] || [];
    const segments: IPricingSegment[] = [];
    for (const seg of resolvedLane) {
      const amount = toNumber(seg.price);
      if (amount == null || amount <= 0) continue;
      segments.push({
        scheme: lane.kind,
        index: seg.index,
        amount,
        period: formatPeriod(seg.startDate, seg.endDate, locale),
        fraction: span && span > 0 ? seg.days / span : 0,
        monthlyRate: rate(amount, seg.days),
      });
    }
    if (segments.length === 0) continue;
    const total = segments.reduce((sum, s) => sum + s.amount, 0);
    tiers.push({ scheme: lane.kind, segments, total, isBest: false, extraVsBest: 0 });
  }

  // "Best value" is the CHEAPEST way to cover the season, compared across every
  // lane — not simply the full-season lane. When a plan prices its splits
  // proportionally (2 x 5000 against a 10000 season) the lanes tie and all are
  // flagged; a lane that costs more must never carry the badge.
  if (tiers.length > 0) {
    const bestTotal = Math.min(...tiers.map((tier) => tier.total));
    for (const tier of tiers) {
      const extra = tier.total - bestTotal;
      tier.isBest = extra <= PRICE_EPSILON;
      tier.extraVsBest = tier.isBest ? 0 : extra;
    }
  }

  return tiers;
};

/**
 * The minimum numeric price across the per-session price and all memberships,
 * for a compact "from X" card label. Returns null when nothing is priced.
 */
export const lowestPrice = (course: ICourse): number | null => {
  const values: number[] = [];

  const perSession = toNumber(course.pricePerSession);
  if (perSession != null) values.push(perSession);

  for (const membership of course.memberships || []) {
    const priceOptions = membership.priceOptions;
    if (priceOptions && priceOptions.length > 0) {
      for (const option of priceOptions) {
        if (Number.isFinite(option.amount)) values.push(option.amount);
      }
      continue;
    }
    const price = toNumber(membership.price);
    if (price != null) values.push(price);
  }

  return values.length > 0 ? Math.min(...values) : null;
};

/**
 * Whether a course offers at least one paid membership option — used to decide
 * whether joining should surface a payment prompt (the Join → Pay bridge).
 * Free / per-session-only courses return false.
 */
export const hasPaidMembership = (course: ICourse): boolean =>
  (course.memberships || []).some((membership) => {
    const priceOptions = membership.priceOptions;
    if (priceOptions && priceOptions.length > 0) {
      return priceOptions.some((opt) => Number.isFinite(opt.amount) && opt.amount > 0);
    }
    const price = toNumber(membership.price);
    return price != null && price > 0;
  });
